import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  Label,
  ListInput,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from '@ais-ui/ui-core';
import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useLocalization } from '@ais-ui/locale-management';
import { PiFlag, PiPhone } from 'react-icons/pi';
import { useGetReportExportFormats } from '@ais-ui/websockets';

import { FilterType, ReportType, ShapeType } from '../../reportsTypes';
import { ReportCloneValues } from '../utils/createReportCloneFormSchema';
import { FormFieldTypes } from '../utils/formFieldTypes';
import { FilterFormField } from './FilterFormField';
import { ShapeFormField } from './ShapeFormField';
import { ShipFormField } from './ShipFormField';
import { MiscellaneousFormField } from './MiscellaneousFormField';
import { ProfileSelect } from '../components/ProfileSelect';
import { GroupMultiSelect } from '../../../Groups/GroupMultiSelect';
import { DateTimePickerField } from '../../../utils/DatetimePickerField';
import { getMailActionOptions } from '../utils/mailActionMapping';

interface CloneReportFormProps {
  form: UseFormReturn<ReportCloneValues>;
  reportType: ReportType;
  filterType: FilterType;
  setFilterType: (filterType: FilterType) => void;
  shapeType: ShapeType;
  setShapeType: (shapeType: ShapeType) => void;
  databaseRange: { database: { begin: Date; end: Date } } | undefined;
  onSubmit: (data: ReportCloneValues) => void;
  isSubmitting: boolean;
}

export const CloneReportForm = ({
  form,
  reportType,
  filterType,
  setFilterType,
  shapeType,
  setShapeType,
  databaseRange,
  onSubmit,
  isSubmitting,
}: CloneReportFormProps) => {
  const { messages } = useLocalization();
  const phoneActive = false;

  // Fetch available export formats for this report type
  const { data: exportFormats } = useGetReportExportFormats(reportType.type);

  // Get mail action options based on available export formats
  const mailActionOptions = useMemo(
    () => getMailActionOptions(exportFormats),
    [exportFormats]
  );

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col h-[654px]"
      >
        <div className="flex-grow">
          <ScrollArea className="flex-grow h-[400px] md:h-[500px] lg:h-[574px] pr-4">
            <div className="space-y-6 px-px">
              <FormField
                control={form.control}
                name="report_name"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex justify-between">
                      <FormLabel>{messages.reportsCloneReportNameLabel}</FormLabel>
                      <InfoIconTooltip>
                        {messages.reportsCloneReportNameTooltip}
                      </InfoIconTooltip>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      <Input
                        type="text"
                        placeholder={messages.reportsCloneReportNamePlaceholder}
                        autoFocus
                        {...field}
                        value={FormFieldTypes.asString(field)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="start_datetime"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>
                      {messages.reportsCreateReportStartDateLabel || 'Start Date'} *
                    </FormLabel>
                    <FormControl>
                      <DateTimePickerField
                        value={FormFieldTypes.asDate(field)}
                        onChange={field.onChange}
                        dateLimit="past"
                        startDatetimeThreshold={databaseRange?.database.begin}
                        endDatetimeThreshold={databaseRange?.database.end}
                        className="w-80"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_datetime"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>
                      {messages.reportsCreateReportEndDateLabel || 'End Date'} *
                    </FormLabel>
                    <FormControl>
                      <DateTimePickerField
                        value={FormFieldTypes.asDate(field)}
                        onChange={field.onChange}
                        dateLimit="past"
                        startDatetimeThreshold={databaseRange?.database.begin}
                        endDatetimeThreshold={databaseRange?.database.end}
                        className="w-80"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {reportType.filter && (
                <FilterFormField
                  form={form}
                  filterFields={reportType.filter}
                  filterType={filterType}
                  setFilterType={setFilterType}
                />
              )}

              {reportType.ships && (
                <ShipFormField
                  form={form}
                  shipsConfig={reportType.ships}
                />
              )}

              {reportType.shape && (
                <ShapeFormField
                  form={form}
                  shapeFields={reportType.shape}
                  shapeType={shapeType}
                  setShapeType={setShapeType}
                />
              )}

              {reportType.prefilledFieldsMap && (
                <ProfileSelect
                  prefilledFieldsMap={reportType.prefilledFieldsMap}
                  form={form}
                  prefix="miscellaneous."
                />
              )}

              {reportType.miscellaneous && (
                <MiscellaneousFormField
                  form={form}
                  miscellaneousFields={reportType.miscellaneous}
                />
              )}
            </div>

            <Separator className="my-6" />

            <div className="space-y-6">
              <Label className="text-md">
                {messages.reportsCreateReportMiscellaneousNotifyLabel}
              </Label>

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="px-px">
                    <div className="flex justify-between">
                      <FormLabel>
                        {messages.reportsCreateReportMiscellaneousNotifyEmailLabel}
                      </FormLabel>
                      <InfoIconTooltip>
                        {messages.reportsCreateReportMiscellaneousNotifyEmailTooltip}
                      </InfoIconTooltip>
                    </div>
                    <FormControl>
                      <ListInput
                        value={(field.value as string[]) || []}
                        onChange={field.onChange}
                        placeholder={
                          messages.reportsCreateReportMiscellaneousNotifyEmailPlaceholder ||
                          'Enter email and press Enter'
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="mailAction"
                render={({ field }) => (
                  <FormItem className="px-px">
                    <div className="flex justify-between">
                      <FormLabel>
                        {messages.reportsCreateReportMiscellaneousMailActionLabel ||
                          'Email content'}
                      </FormLabel>
                      <InfoIconTooltip>
                        {messages.reportsCreateReportMiscellaneousMailActionTooltip ||
                          'Choose what to include in the email when the report completes'}
                      </InfoIconTooltip>
                    </div>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value as string}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              messages.reportsCreateReportMiscellaneousMailActionPlaceholder ||
                              'Select email content'
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent key={`mail-options-${mailActionOptions.length}`}>
                        {mailActionOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="miscellaneous.selectedGroups"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex justify-between">
                      <FormLabel>
                        {messages.reportsCreateReportMiscellaneousShareWithLabel}
                      </FormLabel>
                      <InfoIconTooltip>
                        {messages.reportsCreateReportMiscellaneousShareWithTooltip}
                      </InfoIconTooltip>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      <GroupMultiSelect
                        {...field}
                        value={FormFieldTypes.asSelectArray(field)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {phoneActive && (
                <div>
                  <div className="flex justify-between mb-2">
                    <FormLabel>
                      {messages.reportsCreateReportMiscellaneousNotifyPhoneLabel}
                    </FormLabel>
                    <InfoIconTooltip>
                      {messages.reportsCreateReportMiscellaneousNotifyPhoneTooltip}
                    </InfoIconTooltip>
                  </div>
                  <div className="flex w-full">
                    <div className="relative w-24">
                      <PiFlag className="absolute left-3 top-5 transform -translate-y-1/2 text-muted-foreground" />
                      <FormField
                        control={form.control}
                        name="countryCode"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl onChange={() => form.clearErrors()}>
                              <Input
                                className="pl-9 rounded-r-none"
                                type="text"
                                {...field}
                                value={FormFieldTypes.asString(field)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="relative w-full">
                      <PiPhone className="absolute left-3 top-5 transform -translate-y-1/2 text-muted-foreground" />
                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl onChange={() => form.clearErrors()}>
                              <Input
                                className="pl-9 rounded-l-none"
                                type="text"
                                {...field}
                                value={FormFieldTypes.asString(field)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        <div className="flex items-center justify-end gap-2 pr-2">
          <Button
            id="clone-submit-button"
            onClick={form.handleSubmit(onSubmit)}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? messages.reportsCloneReportButtonTextSubmitting
              : messages.reportsCloneReportButtonText}
          </Button>
        </div>
      </form>
    </Form>
  );
};
